import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { RegisterData } from '@/types';
import Button from '@/components/ui/Button';
import { Dumbbell, Eye, EyeOff, Sun, Moon } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BASE = import.meta.env.VITE_API_URL || 'https://api.fittrack.site/api';

const registerSchema = z.object({
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(30, 'Username cannot exceed 30 characters')
    .regex(/^[a-zA-Z0-9_]+$/, 'Only letters, numbers, and underscores'),
  email: z.string().email('Please enter a valid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Must contain uppercase, lowercase, and a number'),
  firstName: z.string().min(1, 'First name is required').max(50),
  lastName: z.string().min(1, 'Last name is required').max(50),
});

// Reusable styled input for this page
const Field: React.FC<{
  label: string;
  error?: string;
  children: React.ReactNode;
}> = ({ label, error, children }) => (
  // The label wraps the input so they're properly associated — screen readers
  // announce the field name, and clicking the label focuses the input.
  <label className="block">
    <span className="block text-sm font-medium text-ink-muted mb-1.5">{label}</span>
    {children}
    {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
  </label>
);

const inputCls = (hasError?: boolean) =>
  `block w-full px-4 py-3 bg-subtle border ${hasError ? 'border-red-500/50' : 'border-line'} rounded-xl
   text-ink placeholder-ink-faint text-sm
   focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent/50
   transition-colors`;

const RegisterPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('Creating account...');
  const [serverReady, setServerReady] = useState(false);
  const { register: registerUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as any)?.from?.pathname || '/dashboard';

  const { register, handleSubmit, formState: { errors } } = useForm<RegisterData>({
    resolver: zodResolver(registerSchema),
  });

  // Ping backend on mount so Render wakes up before the user submits
  useEffect(() => {
    axios.get(`${BASE}/health`, { timeout: 60000 })
      .then(() => setServerReady(true))
      .catch(() => setServerReady(true));
  }, []);

  const onSubmit = async (data: RegisterData) => {
    setIsLoading(true);
    setLoadingMsg('Creating account...');
    const hint = setTimeout(() => setLoadingMsg('Waking up server… please wait'), 5000);
    try {
      await registerUser(data);
      clearTimeout(hint);
      navigate(redirectTo, { replace: true });
    } catch (error: any) {
      clearTimeout(hint);

      const status    = error?.response?.status;
      const apiMsg    = error?.response?.data?.message as string | undefined;
      const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
      const isNetwork = error?.message === 'Network Error' || !error?.response;

      let msg: string;

      if (isTimeout || (isNetwork && !status)) {
        msg = 'Server is waking up — please try again in a few seconds.';
      } else if (status === 404) {
        msg = 'Server is starting up — please wait a moment and try again.';
      } else if (status === 400) {
        msg = apiMsg || 'Please check your details and try again.';
      } else if (status === 409 || apiMsg?.includes('already exists')) {
        msg = apiMsg || 'An account with that email or username already exists. Try logging in instead.';
      } else if (status === 429) {
        msg = 'Too many attempts — please wait a minute and try again.';
      } else if (status && status >= 500) {
        msg = 'Server error — please try again in a moment.';
      } else {
        msg = apiMsg || 'Registration failed. Please try again.';
      }

      toast.error(msg, { duration: 5000 });
    } finally {
      setIsLoading(false);
      setLoadingMsg('Creating account...');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-app py-12 px-4">
      {/* Theme toggle — top right */}
      <button
        onClick={toggleTheme}
        className="fixed top-4 right-4 p-2 rounded-xl bg-subtle border border-line text-ink-muted hover:text-ink transition-all z-10"
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-2xl bg-accent/10 border border-accent/20">
              <Dumbbell className="h-10 w-10 text-accent" />
            </div>
          </div>
          <h1 className="text-3xl font-black text-ink">Join FitTrack</h1>
          <p className="mt-2 text-sm text-ink-muted">Create your account and start tracking</p>
        </div>

        {/* Card */}
        <div className="bg-surface border border-line rounded-2xl p-8 shadow-[0_4px_24px_rgba(0,0,0,0.5)]">

          {/* Server waking banner */}
          {!serverReady && (
            <div className="flex items-center gap-3 mb-5 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <svg className="animate-spin h-4 w-4 text-amber-400 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              <p className="text-xs text-amber-300">
                Server is starting up — this takes ~20 seconds on first load.
              </p>
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Name row */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="First Name" error={errors.firstName?.message}>
                <input {...register('firstName')} type="text" autoComplete="given-name"
                  placeholder="John" className={inputCls(!!errors.firstName)} />
              </Field>
              <Field label="Last Name" error={errors.lastName?.message}>
                <input {...register('lastName')} type="text" autoComplete="family-name"
                  placeholder="Doe" className={inputCls(!!errors.lastName)} />
              </Field>
            </div>

            <Field label="Username" error={errors.username?.message}>
              <input {...register('username')} type="text" autoComplete="username"
                placeholder="johndoe" className={inputCls(!!errors.username)} />
            </Field>

            <Field label="Email Address" error={errors.email?.message}>
              <input {...register('email')} type="email" autoComplete="email"
                placeholder="john@example.com" className={inputCls(!!errors.email)} />
            </Field>

            <Field label="Password" error={errors.password?.message}>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Create a strong password"
                  className={inputCls(!!errors.password) + ' pr-11'}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-ink-muted hover:text-ink">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {!errors.password && (
                <p className="mt-1.5 text-xs text-ink-dim">Min 8 chars · uppercase · lowercase · number</p>
              )}
            </Field>

            <Button type="submit" loading={isLoading} className="w-full mt-2" size="lg">
              {isLoading ? loadingMsg : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-ink-dim">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-accent hover:brightness-110 transition-colors">
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        <p className="mt-4 text-center text-xs text-ink-faint">
          By creating an account you agree to our{' '}
          <a href="#" className="text-ink-dim hover:text-ink-muted">Terms</a> and{' '}
          <a href="#" className="text-ink-dim hover:text-ink-muted">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
